import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card } from '@trafiflow/shared-ui'
import {
  ArrowLeft,
  Upload,
  Play,
  Sparkles,
  Layers,
  CheckCircle,
  Download,
  Loader2,
} from 'lucide-react'
import { getAuthToken } from '@trafiflow/shared-ui'
import { matchScenesToFootage, generateRemix, type SceneMatch, type ConfirmedSceneMatch, type AltairStoryboard } from './remix.service'
import { listCompanyVideos, type CompanyVideoAsset } from '../../modules/marketing/pages/repository/companyVideos.service'

type Step = 1 | 2 | 3 | 4

export function AdRemixPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1: Source ad
  const [sourceVideoFile, setSourceVideoFile] = useState<File | null>(null)
  const [sourceStoryboard, setSourceStoryboard] = useState<AltairStoryboard | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  // Step 2: Match scenes
  const [companyVideos, setCompanyVideos] = useState<CompanyVideoAsset[]>([])
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([])
  const [companyVideosLoading, setCompanyVideosLoading] = useState(false)
  const [selectedTagFilter, setSelectedTagFilter] = useState('all')
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all')
  const [sceneMatches, setSceneMatches] = useState<SceneMatch[]>([])

  // Step 3: Confirm matches
  const [confirmedMatches, setConfirmedMatches] = useState<ConfirmedSceneMatch[]>([])

  // Step 4: Generate
  const [remixId, setRemixId] = useState<string | null>(null)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)

  const handleVideoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSourceVideoFile(file)
      setError(null)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('video/')) {
      setSourceVideoFile(file)
      setError(null)
    } else {
      setError('Please drop a valid video file')
    }
  }, [])

  const handleAnalyze = async () => {
    if (!sourceVideoFile) {
      setError('Please select a video file')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      // Step 1a: Get presigned upload URL
      const uploadRes = await fetch(`${import.meta.env.VITE_API_URL}/api/storyboard/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          filename: sourceVideoFile.name,
          contentType: sourceVideoFile.type,
        }),
      })

      if (!uploadRes.ok) {
        throw new Error('Failed to get upload URL')
      }

      const uploadData = await uploadRes.json()

      // Step 1b: Upload to S3
      const uploadXhr = new XMLHttpRequest()
      uploadXhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100))
        }
      })

      uploadXhr.addEventListener('load', async () => {
        if (uploadXhr.status >= 400) {
          throw new Error(`Upload failed: ${uploadXhr.statusText}`)
        }

        // Step 1c: Call storyboard generate
        try {
          const generateRes = await fetch(`${import.meta.env.VITE_API_URL}/api/storyboard/generate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              s3Key: uploadData.s3Key,
              mimeType: sourceVideoFile.type,
            }),
          })

          if (!generateRes.ok) {
            throw new Error('Failed to analyze video')
          }

          const storyboardData = await generateRes.json()
          setSourceStoryboard(storyboardData.storyboard)
          setCurrentStep(2)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to analyze video')
        } finally {
          setLoading(false)
          setUploadProgress(null)
        }
      })

      uploadXhr.addEventListener('error', () => {
        setError('Upload failed')
        setLoading(false)
        setUploadProgress(null)
      })

      uploadXhr.open('PUT', uploadData.uploadUrl)
      uploadXhr.send(sourceVideoFile)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentStep !== 2) {
      return
    }

    let cancelled = false

    const loadCompanyVideos = async () => {
      setCompanyVideosLoading(true)
      setError(null)

      try {
        const loadedVideos: CompanyVideoAsset[] = []
        let cursor: string | undefined

        do {
          const res = await listCompanyVideos({ limit: 200, cursor })
          loadedVideos.push(...res.videos)
          cursor = res.nextCursor
        } while (cursor)

        if (cancelled) {
          return
        }

        const readyVideos = loadedVideos.filter((video) => video.status === 'ready')
        setCompanyVideos(readyVideos)
        setSelectedVideoIds((prev) =>
          prev.filter((videoId) => readyVideos.some((video) => video.id === videoId))
        )
      } catch (err) {
        if (!cancelled) {
          setCompanyVideos([])
          setSelectedVideoIds([])
          setError(err instanceof Error ? err.message : 'Failed to load company videos')
        }
      } finally {
        if (!cancelled) {
          setCompanyVideosLoading(false)
        }
      }
    }

    void loadCompanyVideos()

    return () => {
      cancelled = true
    }
  }, [currentStep])

  const categoryOptions = useMemo(() => {
    return Array.from(new Set(companyVideos.map((video) => video.category).filter(Boolean) as string[]))
      .sort((a, b) => a.localeCompare(b))
  }, [companyVideos])

  const tagOptions = useMemo(() => {
    return Array.from(
      new Set(
        companyVideos
          .flatMap((video) => video.tags || [])
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b))
  }, [companyVideos])

  const filteredCompanyVideos = useMemo(() => {
    return companyVideos.filter((video) => {
      const matchesCategory =
        selectedCategoryFilter === 'all' || video.category === selectedCategoryFilter
      const matchesTag = selectedTagFilter === 'all' || (video.tags || []).includes(selectedTagFilter)
      return matchesCategory && matchesTag
    })
  }, [companyVideos, selectedCategoryFilter, selectedTagFilter])

  const handleMatchScenes = async () => {
    if (!sourceStoryboard || selectedVideoIds.length === 0) {
      setError('No repository videos selected')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const matches = await matchScenesToFootage(sourceStoryboard, selectedVideoIds)
      setSceneMatches(matches.matches)

      // Initialize confirmed matches with AI suggestions
      const initial: ConfirmedSceneMatch[] = matches.matches.map((match) => ({
        sceneNumber: match.sceneNumber,
        videoId: match.suggestions[0]?.videoId || '',
        trimStart: match.suggestions[0]?.suggestedTrimStart || 0,
        trimEnd: match.suggestions[0]?.suggestedTrimEnd || match.sceneDuration,
      }))
      setConfirmedMatches(initial)

      setCurrentStep(3)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match scenes')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateRemix = async () => {
    if (!sourceStoryboard || confirmedMatches.length === 0) {
      setError('No scene matches confirmed')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await generateRemix(sourceStoryboard, confirmedMatches)
      setRemixId(result.remixId)
      setCurrentStep(4)

      // In POC, the status is "coming_soon", so no download URL yet
      if (result.downloadUrl) {
        setDownloadUrl(result.downloadUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate remix')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank')
    }
  }

  const resetWizard = () => {
    setCurrentStep(1)
    setSourceVideoFile(null)
    setSourceStoryboard(null)
    setUploadProgress(null)
    setCompanyVideos([])
    setSelectedVideoIds([])
    setSelectedTagFilter('all')
    setSelectedCategoryFilter('all')
    setSceneMatches([])
    setConfirmedMatches([])
    setRemixId(null)
    setDownloadUrl(null)
    setError(null)
  }

  return (
    <div className="min-h-full bg-tf-bg-muted">
      {/* Header */}
      <div className="bg-tf-bg-card border-b border-tf-border px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/marketing/playground')}
            className="flex items-center gap-2 text-tf-text-muted hover:text-tf-text mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Playground</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-tf-text">Ad Remix Engine</h1>
              <p className="text-tf-text-muted mt-1">
                Turn your winning ad into a new variation using your footage
              </p>
            </div>
          </div>

          {/* Stepper */}
          <div className="flex items-center gap-4 mt-6">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-tf-bg-hover text-tf-text-muted'
                  }`}
                >
                  {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                </div>
                {step < 4 && (
                  <div
                    className={`w-16 h-0.5 mx-2 ${
                      step < currentStep ? 'bg-blue-600' : 'bg-tf-bg-hover'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-tf-feedback-error-bg border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Step 1: Select Source Ad */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <Card className="p-8">
              <h2 className="text-lg font-semibold text-tf-text mb-4">
                Step 1: Select Your Winning Ad
              </h2>
              <p className="text-tf-text-muted mb-6">
                Upload the ad that you want to remix. We'll analyze its scene structure and help you
                create a new variation using your own footage.
              </p>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-tf-border rounded-lg p-12 text-center hover:border-blue-500 transition-colors"
              >
                <Upload className="w-12 h-12 mx-auto text-tf-text-muted mb-4" />
                <p className="text-lg font-medium text-tf-text mb-2">
                  {sourceVideoFile ? sourceVideoFile.name : 'Drop your video here'}
                </p>
                <p className="text-tf-text-muted mb-4">or</p>
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="hidden"
                  />
                  <Button variant="primary">Choose File</Button>
                </label>
                {sourceVideoFile && (
                  <p className="mt-4 text-sm text-tf-text-muted">
                    {(sourceVideoFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>

              {uploadProgress !== null && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-tf-bg-hover rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </Card>

            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={handleAnalyze}
                disabled={!sourceVideoFile || loading}
                icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              >
                {loading ? 'Analyzing...' : 'Analyze Ad'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Match Scenes */}
        {currentStep === 2 && sourceStoryboard && (
          <div className="space-y-6">
            <Card className="p-8">
              <h2 className="text-lg font-semibold text-tf-text mb-4">
                Step 2: Review Storyboard Analysis
              </h2>

              <div className="mb-6">
                <p className="text-tf-text-muted mb-4">
                  We analyzed your ad and found {sourceStoryboard.scenes.length} scenes. Now let's
                  match each scene with clips from your footage repository.
                </p>

                {/* Storyboard scenes */}
                <div className="flex gap-4 overflow-x-auto pb-4">
                  {sourceStoryboard.scenes.map((scene) => (
                    <div
                      key={scene.sceneNumber}
                      className="flex-shrink-0 w-48 bg-tf-bg-hover rounded-lg p-4"
                    >
                      <div className="text-sm font-medium text-tf-text-muted mb-2">
                        Scene {scene.sceneNumber}
                      </div>
                      <div className="font-medium text-tf-text mb-2">
                        {scene.visualType || 'Scene'}
                      </div>
                      <div className="text-sm text-tf-text-muted line-clamp-3">
                        {scene.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Repository video gallery */}
              <div className="space-y-4">
                {companyVideosLoading ? (
                  <div className="rounded-lg border border-tf-border bg-tf-bg-muted p-6 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-tf-text-muted mx-auto mb-2" />
                    <p className="text-sm text-tf-text-muted">Loading company videos...</p>
                  </div>
                ) : companyVideos.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-tf-border bg-tf-bg-muted p-6 text-center">
                    <p className="text-tf-text-sub mb-2">No company videos found.</p>
                    <button
                      type="button"
                      onClick={() => navigate('/marketing/repository/footage')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Upload footage in Repository
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border border-tf-border p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-tf-text-sub mb-1">
                            Filter by category
                          </label>
                          <select
                            value={selectedCategoryFilter}
                            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-tf-border rounded-lg bg-tf-bg-card"
                          >
                            <option value="all">All categories</option>
                            {categoryOptions.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-tf-text-sub mb-1">
                            Filter by tag
                          </label>
                          <select
                            value={selectedTagFilter}
                            onChange={(e) => setSelectedTagFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-tf-border rounded-lg bg-tf-bg-card"
                          >
                            <option value="all">All tags</option>
                            {tagOptions.map((tag) => (
                              <option key={tag} value={tag}>
                                {tag}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-tf-text-muted">
                          {selectedVideoIds.length} of {companyVideos.length} videos selected
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              setSelectedVideoIds((prev) =>
                                Array.from(new Set([...prev, ...filteredCompanyVideos.map((video) => video.id)]))
                              )
                            }
                          >
                            Select All
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const filteredIds = new Set(filteredCompanyVideos.map((video) => video.id))
                              setSelectedVideoIds((prev) => prev.filter((videoId) => !filteredIds.has(videoId)))
                            }}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>
                    </div>

                    {filteredCompanyVideos.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-tf-border p-6 text-center">
                        <p className="text-sm text-tf-text-muted">
                          No videos match the selected filters.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredCompanyVideos.map((video) => {
                          const isSelected = selectedVideoIds.includes(video.id)

                          return (
                            <div
                              key={video.id}
                              className={`rounded-lg border p-4 ${
                                isSelected
                                  ? 'border-blue-500 bg-tf-feedback-info-bg'
                                  : 'border-tf-border bg-tf-bg-card'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <h3 className="font-medium text-tf-text line-clamp-2">
                                    {video.title || video.fileName}
                                  </h3>
                                  <p className="text-sm text-tf-text-muted truncate mt-1">
                                    {video.fileName}
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    setSelectedVideoIds((prev) =>
                                      prev.includes(video.id)
                                        ? prev.filter((videoId) => videoId !== video.id)
                                        : [...prev, video.id]
                                    )
                                  }
                                  className="mt-1 h-4 w-4 rounded border-tf-border text-blue-600"
                                />
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {video.category && (
                                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                                    {video.category}
                                  </span>
                                )}
                                {(video.tags || []).map((tag) => (
                                  <span
                                    key={`${video.id}-${tag}`}
                                    className="inline-flex items-center rounded-full bg-tf-bg-hover px-2 py-1 text-xs text-tf-text-sub"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setCurrentStep(1)}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleMatchScenes}
                disabled={selectedVideoIds.length === 0 || loading || companyVideosLoading}
                icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
              >
                {loading ? 'Matching Scenes...' : 'Match with My Footage'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm Matches */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <Card className="p-8">
              <h2 className="text-lg font-semibold text-tf-text mb-4">
                Step 3: Review AI Suggestions
              </h2>
              <p className="text-tf-text-muted mb-6">
                We've matched each scene with clips from your repository. You can swap any suggestion
                with a different clip.
              </p>

              {/* Match comparison */}
              <div className="space-y-4">
                {sceneMatches.map((match) => (
                  <div key={match.sceneNumber} className="flex items-center gap-4 p-4 bg-tf-bg-muted rounded-lg">
                    <div className="w-16 text-center">
                      <div className="text-lg font-bold text-tf-text">{match.sceneNumber}</div>
                      <div className="text-xs text-tf-text-muted">{match.sceneDuration.toFixed(1)}s</div>
                    </div>

                    <div className="flex-1">
                      <div className="text-sm text-tf-text-muted mb-1">Suggested</div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`px-2 py-1 rounded text-sm font-medium ${
                            match.suggestions[0]?.confidence >= 0.8
                              ? 'bg-green-100 text-green-800'
                              : match.suggestions[0]?.confidence >= 0.6
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {((match.suggestions[0]?.confidence || 0) * 100).toFixed(0)}%
                        </div>
                        <span className="text-tf-text">
                          {match.suggestions[0]?.videoTitle || 'Unknown'}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        // In POC, just cycle through suggestions
                        const currentIndex = confirmedMatches.findIndex(
                          (m) => m.sceneNumber === match.sceneNumber
                        )
                        const suggestions = match.suggestions
                        const nextIndex = (currentIndex + 1) % suggestions.length
                        const newMatches = [...confirmedMatches]
                        newMatches[currentIndex] = {
                          sceneNumber: match.sceneNumber,
                          videoId: suggestions[nextIndex]?.videoId || '',
                          trimStart: suggestions[nextIndex]?.suggestedTrimStart || 0,
                          trimEnd: suggestions[nextIndex]?.suggestedTrimEnd || match.sceneDuration,
                        }
                        setConfirmedMatches(newMatches)
                      }}
                    >
                      Swap
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setCurrentStep(2)}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerateRemix}
                disabled={loading}
                icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              >
                {loading ? 'Generating...' : 'Generate Remix'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Download */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <Card className="p-8 text-center">
              <h2 className="text-lg font-semibold text-tf-text mb-4">Step 4: Your Remix is Ready!</h2>

              {remixId && (
                <div className="mb-6">
                  <div className="text-sm text-tf-text-muted mb-2">Remix ID</div>
                  <div className="text-2xl font-mono text-tf-text">{remixId}</div>
                </div>
              )}

              {downloadUrl ? (
                <div className="space-y-6">
                  {/* Video Preview */}
                  <div className="max-w-2xl mx-auto rounded-lg overflow-hidden bg-black">
                    <video
                      controls
                      autoPlay
                      className="w-full"
                      src={downloadUrl}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <Button variant="primary" size="lg" icon={<Download className="w-5 h-5" />} onClick={handleDownload}>
                    Download Remix
                  </Button>
                </div>
              ) : (
                <div className="p-6 bg-tf-feedback-info-bg rounded-lg">
                  <Play className="w-12 h-12 mx-auto text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold text-tf-text mb-2">Generating...</h3>
                  <p className="text-tf-text-muted max-w-md mx-auto">
                    Your remix is being generated. This should only take a moment.
                  </p>
                </div>
              )}
            </Card>

            <div className="flex justify-center gap-4">
              <Button variant="secondary" onClick={() => setCurrentStep(3)}>
                Edit Matches
              </Button>
              <Button variant="primary" onClick={resetWizard}>
                Create Another Remix
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdRemixPage
